import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faHistory, 
  faComment, 
  faQuestionCircle, 
  faFileText, 
  faCircle, 
  faCalendarAlt, 
  faClock,
  faComments
} from '@fortawesome/free-solid-svg-icons';
import { chatAPI } from '../../../services/api';
import { Skeleton } from '../../Skeleton';
import './ChatHistory.css';

interface ConversationHistory {
  id: number;
  question: string;
  rag_answer: string;
  basic_answer: string;
  selected_answer?: string;
  created_at: string;
  feedback?: string;
}

interface ConversationThread {
  threadId: string;
  conversations: ConversationHistory[];
  startTime: Date;
  endTime: Date;
}

export const ChatHistory: React.FC = () => {
  const [conversationHistory, setConversationHistory] = useState<ConversationHistory[]>([]);
  const [conversationThreads, setConversationThreads] = useState<ConversationThread[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [fontScale, setFontScale] = useState<number>(() => {
    const stored = localStorage.getItem('chat-font-scale');
    return stored ? parseFloat(stored) : 1;
  });

  useEffect(() => {
    loadConversationHistory();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const increaseFont = () => {
    const newScale = Math.min(fontScale + 0.1, 1.5);
    setFontScale(newScale);
    localStorage.setItem('chat-font-scale', newScale.toString());
  };

  const decreaseFont = () => {
    const newScale = Math.max(fontScale - 0.1, 0.8);
    setFontScale(newScale);
    localStorage.setItem('chat-font-scale', newScale.toString());
  };

  // Group conversations into threads based on time proximity
  const groupConversationsIntoThreads = (conversations: ConversationHistory[]): ConversationThread[] => {
    if (conversations.length === 0) return [];

    // Sort conversations by creation time (newest first)
    const sortedConversations = [...conversations].sort((a, b) => 
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );

    const threads: ConversationThread[] = [];
    const THREAD_TIME_WINDOW = 30 * 60 * 1000; // 30 minutes in milliseconds

    sortedConversations.forEach((conv) => {
      const convTime = new Date(conv.created_at);
      
      // Find if this conversation belongs to an existing thread
      let belongsToThread = false;
      
      for (const thread of threads) {
        const timeDiff = Math.abs(convTime.getTime() - thread.endTime.getTime());
        
        if (timeDiff <= THREAD_TIME_WINDOW) {
          // Add to existing thread
          thread.conversations.push(conv);
          thread.startTime = convTime < thread.startTime ? convTime : thread.startTime;
          thread.endTime = convTime > thread.endTime ? convTime : thread.endTime;
          belongsToThread = true;
          break;
        }
      }
      
      if (!belongsToThread) {
        // Create new thread
        threads.push({
          threadId: `thread-${conv.id}-${convTime.getTime()}`,
          conversations: [conv],
          startTime: convTime,
          endTime: convTime
        });
      }
    });

    // Sort conversations within each thread by time (oldest first for better reading flow)
    threads.forEach(thread => {
      thread.conversations.sort((a, b) => 
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      );
    });

    return threads;
  };

  const loadConversationHistory = async () => {
    try {
      setLoading(true);
      const history = await chatAPI.getConversationHistory();
      setConversationHistory(history);
      
      // Group conversations into threads
      const threads = groupConversationsIntoThreads(history);
      setConversationThreads(threads);
    } catch (err) {
      setError('Błąd podczas ładowania historii rozmów');
      console.error('Error loading conversation history:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="history-container" style={{ fontSize: `${fontScale}rem` }}>
        <div className="history-section">
          <div className="history-header">
            <div className="history-header-text">
              <h3><FontAwesomeIcon icon={faHistory} className="header-icon" /> Historia Twoich rozmów</h3>
            </div>
            <div className="history-controls">
              <button type="button" className="text-size-button" onClick={increaseFont} aria-label="Zwiększ rozmiar tekstu">A+</button>
              <button type="button" className="text-size-button" onClick={decreaseFont} aria-label="Zmniejsz rozmiar tekstu">A-</button>
            </div>
          </div>
          <div className="history-content">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="conversation-item">
                <Skeleton height="120px" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="history-container" style={{ fontSize: `${fontScale}rem` }}>
        <div className="error-message" role="alert">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="history-container" style={{ fontSize: `${fontScale}rem` }}>
      <div className="history-section" tabIndex={0} aria-label="Historia rozmów">
        <div className="history-header">
          <div className="history-header-text">
            <h3><FontAwesomeIcon icon={faHistory} className="header-icon" /> Historia Twoich rozmów</h3>
            <p className="history-subtitle">Tutaj znajdziesz wszystkie swoje poprzednie pytania i odpowiedzi</p>
          </div>
          <div className="history-controls">
            <button type="button" className="text-size-button" onClick={increaseFont} aria-label="Zwiększ rozmiar tekstu">A+</button>
            <button type="button" className="text-size-button" onClick={decreaseFont} aria-label="Zmniejsz rozmiar tekstu">A-</button>
          </div>
        </div>
        {conversationThreads.length === 0 ? (
          <div className="no-conversations">
            <div className="empty-icon"><FontAwesomeIcon icon={faHistory} /></div>
            <p>Nie masz jeszcze żadnych rozmów</p>
            <p className="empty-hint">Zadaj pierwsze pytanie w czacie, aby rozpocząć!</p>
            <a href="/chat" className="start-chat-button">
              <span className="button-icon"><FontAwesomeIcon icon={faComment} /></span>
              <span className="button-text">Przejdź do czatu</span>
            </a>
          </div>
        ) : (
          <div className="conversations-list">
            <div className="conversations-count">
              <p>Łącznie przeprowadziłeś <strong>{conversationHistory.length}</strong> rozmów pogrupowanych w <strong>{conversationThreads.length}</strong> sesji</p>
            </div>
            {conversationThreads.map((thread, threadIndex) => (
              <div key={thread.threadId} className="conversation-thread">
                <div className="thread-header">
                  <div className="thread-title">
                    <span className="thread-icon"><FontAwesomeIcon icon={faComments} /></span>
                    <span className="thread-label">
                      {thread.conversations.length === 1 ? 'Pojedyncze pytanie' : `Sesja rozmowy (${thread.conversations.length} pytań)`}
                    </span>
                  </div>
                  <div className="thread-meta">
                    <span className="thread-date">
                      <FontAwesomeIcon icon={faCalendarAlt} /> {thread.startTime.toLocaleDateString('pl-PL', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                      {thread.conversations.length > 1 && thread.startTime.getTime() !== thread.endTime.getTime() && (
                        <span className="thread-duration">
                          {' '}- {thread.endTime.toLocaleDateString('pl-PL', { 
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      )}
                    </span>
                  </div>
                </div>
                
                <div className="thread-conversations">
                  {thread.conversations.map((conv, convIndex) => (
                    <div key={conv.id} className="conversation-item">
                      {thread.conversations.length > 1 && (
                        <div className="conversation-sequence">
                          <span className="sequence-number">#{convIndex + 1}</span>
                          <span className="sequence-time">
                            <FontAwesomeIcon icon={faClock} /> {new Date(conv.created_at).toLocaleTimeString('pl-PL', { 
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                          {conv.selected_answer && (
                            <span className={`badge ${conv.selected_answer === 'rag' ? 'rag' : 'basic'}`}>
                              <FontAwesomeIcon icon={conv.selected_answer === 'rag' ? faFileText : faCircle} /> {conv.selected_answer === 'rag' ? 'Z bazą wiedzy' : 'Podstawowa'}
                            </span>
                          )}
                        </div>
                      )}
                      
                      <div className="conversation-content">
                        <div className="question-display">
                          <h4><FontAwesomeIcon icon={faQuestionCircle} /> {thread.conversations.length > 1 ? `Pytanie ${convIndex + 1}:` : 'Twoje pytanie:'}</h4>
                          <p className="question-text">{conv.question}</p>
                        </div>
                        <div className="answers-display">
                          <div className="answer-section rag-answer">
                            <h5><FontAwesomeIcon icon={faFileText} /> Odpowiedź z bazą wiedzy:</h5>
                            <div className="answer-text">
                              <ReactMarkdown 
                                components={{
                                  p: ({children}) => <p>{children}</p>,
                                  br: () => <br />,
                                }}
                              >
                                {conv.rag_answer}
                              </ReactMarkdown>
                            </div>
                          </div>
                          <div className="answer-section basic-answer">
                            <h5><FontAwesomeIcon icon={faCircle} /> Odpowiedź podstawowa:</h5>
                            <div className="answer-text">
                              <ReactMarkdown 
                                components={{
                                  p: ({children}) => <p>{children}</p>,
                                  br: () => <br />,
                                }}
                              >
                                {conv.basic_answer}
                              </ReactMarkdown>
                            </div>
                          </div>
                        </div>
                        {conv.feedback && (
                          <div className="feedback-display">
                            <h5><FontAwesomeIcon icon={faFileText} /> Twoja opinia:</h5>
                            <p className="feedback-text">{conv.feedback}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
